import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  approveMaster,
  activateMaster,
  createMaster,
  listMaster,
  proposeMasterChange,
  purgeMaster,
  rejectMaster,
  retireMaster,
  updateMasterDirect,
  type MasterListParams,
  type MasterListResult,
  type ProposeChangeResult,
} from '@/lib/api-masters';
import type { Material, MaterialCreateInput, MaterialUpdateInput } from './types';

const KEY = 'materials' as const;

export const useMaterialsList = (params: MasterListParams = {}) => {
  return useQuery<MasterListResult<Material>, ApiClientError>({
    queryKey: [KEY, 'list', params],
    queryFn: () => listMaster<Material>('materials', params),
    placeholderData: (prev) => prev,
  });
};

export const useCreateMaterial = () => {
  const qc = useQueryClient();
  return useMutation<Material, ApiClientError, MaterialCreateInput>({
    mutationFn: (body) => createMaster<Material, MaterialCreateInput>('materials', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useApproveMaterial = () => {
  const qc = useQueryClient();
  return useMutation<Material, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => approveMaster<Material>('materials', id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useRejectMaterial = () => {
  const qc = useQueryClient();
  return useMutation<Material, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => rejectMaster<Material>('materials', id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useRetireMaterial = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => retireMaster('materials', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useActivateMaterial = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => activateMaster('materials', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const usePurgeMaterial = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => purgeMaster('materials', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

/** Manager-only direct edit. `PUT /api/masters/materials/:id`. */
export const useUpdateMaterialDirect = () => {
  const qc = useQueryClient();
  return useMutation<Material, ApiClientError, { id: string; body: MaterialUpdateInput }>({
    mutationFn: ({ id, body }) =>
      updateMasterDirect<Material, MaterialUpdateInput>('materials', id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

/** Supervisor+ change-proposal. */
export const useProposeMaterialChange = () => {
  const qc = useQueryClient();
  return useMutation<
    ProposeChangeResult,
    ApiClientError,
    { id: string; body: { proposed: MaterialUpdateInput; reason: string } }
  >({
    mutationFn: ({ id, body }) => proposeMasterChange<MaterialUpdateInput>('materials', id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
